import type { Id } from '../../api/types';
import { useProjects } from '../../projects/ProjectsProvider';
import { EmptyState } from '../ui/EmptyState';
import { ErrorNotice, StaleBanner } from '../ui/Notice';
import { SkeletonBar } from '../ui/Skeleton';
import { ProjectOption } from './ProjectOption';

/**
 * The DESKTOP project rail — the left column in Jira's shot 2, minus eleven things we do not have.
 *
 * ⛔ `hidden md:flex`. A permanent 240px rail costs 61% of a 390px phone, so below 768px it is not
 * narrowed, it is REPLACED — by `ProjectSwitcher` in the top bar. See AppShell for why that is a
 * different component rather than the same one with responsive classes.
 *
 * 🔴 Every failure mode is spoken:
 *   • could not load at all → `ErrorNotice` with a Retry, and NO list
 *   • loaded once, refresh failed → `StaleBanner` ABOVE the list it applies to
 *   • loaded and genuinely empty → `EmptyState`
 * ⛔ The one thing it never does is render the last good list as though it were current.
 *
 * ⚠️ HONESTY ABOUT THE STALE BRANCH: today it cannot fire. `useProjects` has empty deps and the only
 * `reload()` is on a Retry that exists only while there is no data, so the resource never holds data
 * AND an error at once. It is written anyway because the day someone adds a refetch — a "new
 * project" action, a poll — the alternative is that the rail silently shows yesterday's list. ⛔ Do
 * not read it as verified behaviour; it is verified UNREACHABLE, which is a different claim.
 */
export function ProjectRail({
  projectId,
  onSelect,
}: {
  projectId: Id | null;
  onSelect: (projectId: Id | null) => void;
}) {
  const projects = useProjects();
  const items = projects.data;

  return (
    <aside
      aria-label="Projects"
      className="hidden w-60 shrink-0 flex-col border-r border-[color:var(--color-line)] bg-[color:var(--color-surface)] md:flex"
    >
      <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-1.5">
        <h2 className="text-xs font-bold tracking-wide text-[color:var(--color-ink-muted)] uppercase">
          Projects
        </h2>
        {items ? (
          <span className="text-xs text-[color:var(--color-ink-subtle)] tabular-nums">
            {items.length}
          </span>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-2 pb-3">
        {projects.isLoading ? (
          <div role="status" aria-label="Loading projects" className="space-y-1.5 px-1 pt-1">
            {[0, 1, 2, 3, 4].map((row) => (
              <SkeletonBar key={row} className="h-9 w-full" />
            ))}
          </div>
        ) : null}

        {projects.error && !projects.isStale ? (
          <ErrorNotice error={projects.error} onRetry={projects.reload} />
        ) : null}

        {projects.isStale && projects.error ? (
          <StaleBanner
            error={projects.error}
            loadedAt={projects.loadedAt}
            onRetry={projects.reload}
          />
        ) : null}

        {items && items.length === 0 && !projects.error ? (
          <EmptyState
            title="No projects yet"
            detail="The request succeeded and returned none — this is an empty result, not a failure."
          />
        ) : null}

        {items && items.length > 0 ? (
          <ul className={projects.isStale ? 'space-y-0.5 opacity-60' : 'space-y-0.5'}>
            <li>
              <ProjectOption
                project={null}
                selected={projectId === null}
                onSelect={() => onSelect(null)}
              />
            </li>
            {items.map((project) => (
              <li key={project.id}>
                <ProjectOption
                  project={project}
                  selected={project.id === projectId}
                  onSelect={() => onSelect(project.id)}
                />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </aside>
  );
}
